import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Sparkles, Heart, MessageCircle, Settings, ArrowRight, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function SanctuaryDashboard() {
  const [membership, setMembership] = useState(null);
  const [prayers, setPrayers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    try {
      const [{ data: m }, { data: p }, { data: r }] = await Promise.all([
        api.get('/membership/me'),
        api.get('/sanctuary/prayers').catch(() => ({ data: [] })),
        api.get('/sanctuary/requests').catch(() => ({ data: [] })),
      ]);
      setMembership(m);
      setPrayers(p);
      setRequests(r);
      if (!m.is_member) {
        // not a member — bounce to landing
        navigate('/sanctuary');
      }
    } catch (err) {
      toast.error('Could not load your Sanctuary');
    } finally {
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post('/membership/portal', { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not open billing portal');
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
        <Header />
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#E57A44' }} />
        </div>
        <Footer />
      </div>
    );
  }

  const periodEnd = membership?.current_period_end ? new Date(membership.current_period_end).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : null;

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="p-8 rounded-3xl mb-8 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFF5E5 0%, #FFFBF3 60%, #F3EFE7 100%)', border: '1px solid #E8C26540' }}>
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="w-6 h-6" style={{ color: '#E57A44' }} />
            <span className="text-xs px-3 py-1 rounded-full" style={{ backgroundColor: '#78A18E', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }} data-testid="sanctuary-status-badge">
              {membership?.status === 'active' ? 'Active' : membership?.status || 'Member'}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }} data-testid="sanctuary-dashboard-title">
            Welcome back, {user?.name?.split(' ')[0] || 'friend'}
          </h1>
          <p style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.7, fontStyle: 'italic' }}>
            "The Lord is close to the brokenhearted and saves those who are crushed in spirit." — Psalm 34:18
          </p>
          {periodEnd && (
            <p className="text-xs mt-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Your Sanctuary renews on <strong>{periodEnd}</strong></p>
          )}
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <ActionCard
            to="/sanctuary/fellowship"
            icon={<Sparkles className="w-6 h-6" />}
            title="Fellowship Channel"
            subtitle={`${prayers.length} ${prayers.length === 1 ? 'voice' : 'voices'} in the circle — insights, wisdom, prayer & encouragement`}
            tint="#E57A44"
            testid="dashboard-goto-fellowship"
          />
          <ActionCard
            to="/sanctuary/requests"
            icon={<MessageCircle className="w-6 h-6" />}
            title="Article Requests"
            subtitle={`${requests.length} idea${requests.length === 1 ? '' : 's'} from the circle`}
            tint="#78A18E"
            testid="dashboard-goto-requests"
          />
        </div>
        <div className="mb-10">
          <ActionCard
            to="/sanctuary/connection-room"
            icon={<Sparkles className="w-6 h-6" />}
            title="The Connection Room"
            subtitle="Weekly reflections, polls, mini challenges & anonymous Q&A — a members-only space to pause and practice"
            tint="#8B6A0A"
            testid="dashboard-goto-connection-room"
          />
        </div>

        {/* Recent activity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          <RecentList title="Latest in Fellowship" items={prayers.slice(0, 3)} icon={<Sparkles className="w-4 h-4" style={{ color: '#E57A44' }} />} emptyText="The circle is quiet. Share the first thought." to="/sanctuary/fellowship" testid="recent-fellowship" />
          <RecentList title="Latest requests" items={requests.slice(0, 3)} icon={<Users className="w-4 h-4" style={{ color: '#78A18E' }} />} emptyText="No article requests yet. Suggest a topic." to="/sanctuary/requests" testid="recent-requests" />
        </div>

        {/* Manage */}
        <div className="p-6 rounded-3xl flex items-center justify-between flex-wrap gap-4" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E6E2D8' }}>
          <div>
            <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Manage your membership</h3>
            <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Update payment method, view invoices, or cancel anytime. Handled securely by Stripe.</p>
          </div>
          <button
            onClick={openPortal}
            disabled={portalLoading}
            className="px-5 py-2.5 rounded-full flex items-center gap-2 transition hover:-translate-y-0.5 disabled:opacity-50"
            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#2C2724', color: '#FDFBF7', fontWeight: 600 }}
            data-testid="dashboard-open-portal"
          >
            <Settings className="w-4 h-4" />
            <span>{portalLoading ? 'Opening…' : 'Manage subscription'}</span>
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function ActionCard({ to, icon, title, subtitle, tint, testid }) {
  return (
    <Link to={to} className="p-6 rounded-3xl flex items-center gap-4 transition hover:-translate-y-1"
      style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.04)', border: '1px solid #F0EBE0' }}
      data-testid={testid}>
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${tint}20`, color: tint }}>
        {icon}
      </div>
      <div className="flex-1">
        <h3 className="text-lg font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{title}</h3>
        <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>{subtitle}</p>
      </div>
      <ArrowRight className="w-5 h-5" style={{ color: tint }} />
    </Link>
  );
}

function RecentList({ title, items, icon, emptyText, to, testid }) {
  return (
    <div className="p-6 rounded-3xl" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0' }} data-testid={testid}>
      <h3 className="text-base font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{icon} {title}</h3>
      {items.length === 0 ? (
        <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{emptyText}</p>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => (
            <li key={it.id} className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>
              <div className="font-semibold">{it.title}</div>
              <div className="text-xs" style={{ color: '#8C867D' }}>{it.user_name || 'A member'}</div>
            </li>
          ))}
        </ul>
      )}
      <Link to={to} className="inline-block mt-4 text-xs underline" style={{ fontFamily: 'Nunito, sans-serif', color: '#E57A44', fontWeight: 600 }}>See all →</Link>
    </div>
  );
}
